import React, { useEffect, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  FormFeedback,
  Container,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import schema from "./schema";
import { toast } from "react-toastify";
import { BackButton } from "components/common/Buttons";
import { getDocumentTitle } from "utils/helpers";
import PageLoadingSpinner from "components/common/PageLoader";
import {
  createSupplier,
  getSupplier,
  updateSupplier,
} from "../services/SupplierService";
import type { Supplier } from "../types/supplier.types";

const SupplierForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(isEdit ? "Edit supplier" : "New supplier");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<Supplier | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(Boolean(isEdit));

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: record?.name ?? "",
      email: record?.email ?? "",
      phone: record?.phone ?? "",
      address: record?.address ?? "",
      city: record?.city ?? "",
      state: record?.state ?? "",
      gstNumber: record?.gstNumber ?? "",
      isActive: record?.isActive !== false,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const payload = {
        name: values.name.trim(),
        email: values.email?.trim() || null,
        phone: values.phone?.trim() || null,
        address: values.address?.trim() || null,
        city: values.city?.trim() || null,
        state: values.state?.trim() || null,
        gstNumber: values.gstNumber?.trim() || null,
        isActive: values.isActive,
      };
      try {
        const res =
          isEdit && id
            ? await updateSupplier(Number(id), payload)
            : await createSupplier(payload);
        if (res.success) {
          toast.success(res.message);
          navigate("/inventory-management/suppliers");
        } else toast.error(res.message);
      } catch (err: unknown) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? "Request failed",
        );
      }
    },
  });

  useEffect(() => {
    if (!isEdit || !id) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingRecord(true);
        const res = await getSupplier(Number(id));
        if (cancelled) return;
        if (res.success && res.data) setRecord(res.data);
      } catch {
        if (!cancelled) setRecord(null);
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, id]);

  if (loadingRecord) {
    return <PageLoadingSpinner message="Loading supplier…" />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? "Edit supplier" : "New supplier"}
          pageTitle="Inventory Management"
          items={[
            {
              label: "Suppliers",
              path: "/inventory-management/suppliers",
            },
          ]}
        />
        <Form
          className="needs-validation"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row className="justify-content-center">
            <Col xl={10}>
              <Card className="shadow-none border">
                <CardHeader className="border-bottom bg-transparent pb-3">
                  <h5 className="card-title mb-1">
                    {isEdit ? "Edit supplier" : "Create supplier"}
                  </h5>
                  <p className="text-muted mb-0 small">
                    Legal name, contact details, and billing address used on
                    purchase orders and GRNs.
                  </p>
                </CardHeader>
                <CardBody className="pt-4">
                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Profile &amp; contact
                  </div>
                  <Row className="g-3 mb-2">
                    <Col xs={12}>
                      <Label className="form-label" htmlFor="supplier-name">
                        Supplier name
                      </Label>
                      <Input
                        id="supplier-name"
                        type="text"
                        name="name"
                        placeholder="e.g. Acme Industrial Supplies"
                        autoComplete="organization"
                        value={validation.values.name}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.name && validation.errors.name,
                        )}
                      />
                      {validation.touched.name && validation.errors.name ? (
                        <FormFeedback type="invalid">
                          {validation.errors.name}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={6}>
                      <Label className="form-label" htmlFor="supplier-email">
                        Email{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Input
                        id="supplier-email"
                        type="email"
                        name="email"
                        placeholder="accounts@example.com"
                        autoComplete="email"
                        value={validation.values.email}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.email && validation.errors.email,
                        )}
                      />
                      {validation.touched.email && validation.errors.email ? (
                        <FormFeedback type="invalid">
                          {validation.errors.email}
                        </FormFeedback>
                      ) : null}
                      <small className="text-muted">
                        Used for correspondence and records.
                      </small>
                    </Col>
                    <Col md={6}>
                      <Label className="form-label" htmlFor="supplier-phone">
                        Phone{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Input
                        id="supplier-phone"
                        type="tel"
                        name="phone"
                        placeholder="Phone number"
                        autoComplete="tel"
                        value={validation.values.phone}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.phone && validation.errors.phone,
                        )}
                      />
                      {validation.touched.phone && validation.errors.phone ? (
                        <FormFeedback type="invalid">
                          {validation.errors.phone}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>

                  <hr className="my-4 text-muted opacity-25" />

                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Address
                  </div>
                  <Row className="g-3 mb-2">
                    <Col xs={12}>
                      <Label className="form-label" htmlFor="supplier-address">
                        Street address{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Input
                        id="supplier-address"
                        type="textarea"
                        rows={3}
                        name="address"
                        placeholder="Building, street, area…"
                        value={validation.values.address}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.address &&
                            validation.errors.address,
                        )}
                      />
                      {validation.touched.address &&
                      validation.errors.address ? (
                        <FormFeedback type="invalid">
                          {validation.errors.address}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={6}>
                      <Label className="form-label" htmlFor="supplier-city">
                        City
                      </Label>
                      <Input
                        id="supplier-city"
                        type="text"
                        name="city"
                        placeholder="City"
                        autoComplete="address-level2"
                        value={validation.values.city}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.city && validation.errors.city,
                        )}
                      />
                      {validation.touched.city && validation.errors.city ? (
                        <FormFeedback type="invalid">
                          {validation.errors.city}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={6}>
                      <Label className="form-label" htmlFor="supplier-state">
                        State
                      </Label>
                      <Input
                        id="supplier-state"
                        type="text"
                        name="state"
                        placeholder="State"
                        autoComplete="address-level1"
                        value={validation.values.state}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.state && validation.errors.state,
                        )}
                      />
                      {validation.touched.state && validation.errors.state ? (
                        <FormFeedback type="invalid">
                          {validation.errors.state}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>

                  <hr className="my-4 text-muted opacity-25" />

                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Tax &amp; status
                  </div>
                  <Row className="g-3 mb-2">
                    <Col md={6}>
                      <Label className="form-label" htmlFor="supplier-gst">
                        GST number{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Input
                        id="supplier-gst"
                        type="text"
                        name="gstNumber"
                        placeholder="e.g. 22AAAAA0000A1Z5"
                        autoComplete="off"
                        value={validation.values.gstNumber}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.gstNumber &&
                            validation.errors.gstNumber,
                        )}
                      />
                      {validation.touched.gstNumber &&
                      validation.errors.gstNumber ? (
                        <FormFeedback type="invalid">
                          {validation.errors.gstNumber}
                        </FormFeedback>
                      ) : null}
                      <small className="text-muted">
                        Include if you need it on tax invoices.
                      </small>
                    </Col>
                    <Col md={6} className="d-flex align-items-end">
                      <div className="w-100 pb-md-1">
                        <div className="form-check form-switch form-switch-md">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="supplier-active"
                            name="isActive"
                            checked={validation.values.isActive}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="supplier-active"
                          >
                            Active supplier
                          </Label>
                        </div>
                        <small className="text-muted d-block mt-1">
                          Inactive suppliers stay in the system but can be hidden
                          from default lists.
                        </small>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                  <BackButton />
                  <Button
                    type="submit"
                    color="primary"
                    className="btn-label px-4"
                    disabled={validation.isSubmitting}
                  >
                    {validation.isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        {isEdit ? "Saving…" : "Creating…"}
                      </>
                    ) : isEdit ? (
                      "Save changes"
                    ) : (
                      "Create supplier"
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default SupplierForm;
